// src/ui/ImportWizard.tsx

import React, { useState, useCallback } from 'react';
import { useProjectStore, createInitialState, saveProjectToDB } from '../state/projectStore';
import { detectMultipleFiles } from '../detection/detectFiles';
import { parseGerberFile } from '../parsers/gerber';
import { parseExcellonFile } from '../parsers/excellon';
import { parsePnPFile } from '../parsers/pnp';
import { parseBOMFile } from '../parsers/bom';
import { generateGroups } from '../grouping/groupPlacements';
import { extractZipFile, isZipFile } from '../utils/zipExtractor';
import { 
  classifyGerberLayer, 
  processGerberLayers, 
  buildLayerStack,
  type GerberCandidate,
  type ConfirmedGerberLayer 
} from '../parsers/gerberDetection';
import { parseGerberToSvg, getLayerColor, getLayerOpacity, parseBoardOutline } from '../parsers/gerberToSvg';
import type { SourceFile, Placement, BOMLine, BoardData, BoardLayer } from '../types';

interface ImportWizardProps {
  onComplete: () => void;
  onCancel: () => void;
}

type WizardStep = 'upload' | 'review' | 'mapping' | 'preview' | 'processing';

interface ProcessingProgress {
  stage: string;
  message: string;
  progress: number; // 0-100
}

export const ImportWizard: React.FC<ImportWizardProps> = ({ onComplete, onCancel }) => {
  const [currentStep, setCurrentStep] = useState<WizardStep>('upload');
  const [files, setFiles] = useState<SourceFile[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState<ProcessingProgress>({
    stage: '',
    message: '',
    progress: 0
  });
  
  const projectStore = useProjectStore();
  
  const handleFileUpload = useCallback(async (uploadedFiles: FileList | null) => {
    if (!uploadedFiles || uploadedFiles.length === 0) return;
    
    setIsProcessing(true);
    setErrors([]);
    
    try {
      const fileArray = Array.from(uploadedFiles);
      const fileData: { name: string; content: string | ArrayBuffer }[] = [];
      
      // Process each file (handle ZIP extraction)
      for (const file of fileArray) {
        const content = await file.arrayBuffer();
        
        // Check if it's a ZIP file
        if (isZipFile(file.name)) {
          try {
            // Extract ZIP contents
            const extractedFiles = await extractZipFile(content);
            
            // Add extracted files to the list
            for (const extracted of extractedFiles) {
              fileData.push({
                name: extracted.name,
                content: extracted.content
              });
            }
            
            console.log(`Extracted ${extractedFiles.length} files from ${file.name}`);
          } catch (zipError) {
            setErrors(prev => [...prev, `Failed to extract ${file.name}: ${zipError instanceof Error ? zipError.message : 'Unknown error'}`]);
          }
        } else {
          // Regular file, add directly
          fileData.push({
            name: file.name,
            content
          });
        }
      }
      
      // Detect file types
      const detection = detectMultipleFiles(fileData);
      
      setFiles(detection.files);
      
      if (detection.errors.length > 0) {
        setErrors(detection.errors.map(e => e.message));
      }
      
      // Move to review step
      setCurrentStep('review');
      
    } catch (error) {
      setErrors([error instanceof Error ? error.message : 'Upload failed']);
    } finally {
      setIsProcessing(false);
    }
  }, []);
  
  const handleProcessFiles = async () => {
    setCurrentStep('processing');
    setIsProcessing(true);
    setProcessingProgress({ stage: 'init', message: 'Initializing project...', progress: 0 });
    
    try {
      // Initialize new project
      const project = createInitialState('New Project');
      project.files = files;
      
      setProcessingProgress({ stage: 'detect', message: 'Detecting Gerber layers...', progress: 10 });
      
      // Parse Gerber files using comprehensive detection and layer stacking
      const gerberFiles = files.filter(f => f.detectedRole.startsWith('gerber-'));
      const gerberCandidates: GerberCandidate[] = [];
      
      for (const file of gerberFiles) {
        const content = typeof file.content === 'string' 
          ? file.content 
          : new TextDecoder().decode(file.content);
        
        gerberCandidates.push({
          id: file.id,
          name: file.name,
          path: file.path || './',
          content,
          size: file.size
        });
      }
      
      setProcessingProgress({ stage: 'classify', message: 'Classifying layers...', progress: 20 });
      
      // Process Gerber layers with classification and stacking
      const boardLayerStack = processGerberLayers(gerberCandidates);
      
      setProcessingProgress({ stage: 'gerber', message: 'Parsing Gerber files...', progress: 30 });
      
      // Parse each layer to SVG
      const allLayers: ConfirmedGerberLayer[] = [];
      for (const layer of [...boardLayerStack.top.layers, ...boardLayerStack.bottom.layers]) {
        const parsed = parseGerberToSvg(layer);
        layer.svgContent = parsed.svgContent;
        allLayers.push(layer);
      }
      
      setProcessingProgress({ stage: 'drills', message: 'Parsing drill files...', progress: 50 });
      
      // Convert to BoardLayer format
      const boardLayers: BoardLayer[] = allLayers.map(layer => ({
        id: layer.id,
        name: layer.name,
        role: layer.role,
        side: layer.side,
        svgContent: layer.svgContent,
        color: getLayerColor(layer.role),
        opacity: getLayerOpacity(layer.role),
        visible: true
      }));
      
      // Parse Excellon drill files
      const drillFiles = files.filter(f => f.detectedRole.startsWith('excellon-'));
      const drills: BoardData['drills'] = [];
      
      for (const file of drillFiles) {
        const content = typeof file.content === 'string' 
          ? file.content 
          : new TextDecoder().decode(file.content);
        
        const result = await parseExcellonFile(content, file.name);
        
        if (result.success) {
          drills.push(...result.data.drills);
        } else {
          file.parseErrors.push(...result.error.map(e => e.message));
        }
      }
      
      setProcessingProgress({ stage: 'pnp', message: 'Parsing pick-and-place files...', progress: 60 });
      
      // Parse PnP files
      const pnpFiles = files.filter(f => f.detectedRole.startsWith('pnp-'));
      const placements: Placement[] = [];
      
      for (const file of pnpFiles) {
        const content = typeof file.content === 'string' 
          ? file.content 
          : new TextDecoder().decode(file.content);
        
        const result = await parsePnPFile(content, file.name, {
          autoDetect: true
        });
        
        if (result.success) {
          // Convert RawPlacement to Placement
          for (const raw of result.data.placements) {
            placements.push({
              ...raw,
              id: `pl_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
              status: 'pending'
            });
          }
        } else {
          file.parseErrors.push(...result.error.map(e => e.message));
        }
      }
      
      setProcessingProgress({ stage: 'bom', message: 'Parsing BOM files...', progress: 70 });
      
      // Parse BOM files
      const bomFiles = files.filter(f => f.detectedRole.startsWith('bom-'));
      const bomLines: BOMLine[] = [];
      
      for (const file of bomFiles) {
        const result = await parseBOMFile(file.content, file.name, {
          autoDetect: true
        });
        
        if (result.success) {
          // Convert RawBOMLine to BOMLine
          for (const raw of result.data.lines) {
            bomLines.push({
              ...raw,
              id: `bom_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
              identityKey: raw.mpn || `${raw.value}_${raw.footprint}`,
              normalizedValue: raw.value ? { raw: raw.value, numeric: 0, unit: 'other', normalized: raw.value } : undefined,
              normalizedFootprint: raw.footprint
            });
          }
        } else {
          file.parseErrors.push(...result.error.map(e => e.message));
        }
      }
      
      // Correlate PnP with BOM
      const placementMap = new Map(placements.map(p => [p.refDes, p]));
      
      for (const bomLine of bomLines) {
        for (const refDes of bomLine.refDesList) {
          const placement = placementMap.get(refDes);
          if (placement) {
            // Enrich placement with BOM data
            placement.bomLineId = bomLine.id;
            placement.mpn = bomLine.mpn;
            placement.manufacturer = bomLine.manufacturer;
            placement.description = bomLine.description;
            placement.voltage = bomLine.voltage;
            placement.tolerance = bomLine.tolerance;
            placement.dielectric = bomLine.dielectric;
          }
        }
      }
      
      setProcessingProgress({ stage: 'groups', message: 'Generating placement groups...', progress: 80 });
      
      // Generate groups
      const { generateGroups, DEFAULT_GROUPING_OPTIONS } = await import('../grouping/groupPlacements');
      const groups = generateGroups(placements, DEFAULT_GROUPING_OPTIONS);
      
      // Update project
      project.board = {
        width: 100, // TODO: Calculate from Gerber
        height: 100,
        layers: boardLayers,
        drills,
        outline: []
      };
      project.placements = placements;
      project.bomLines = bomLines;
      project.groups = groups;
      
      setProcessingProgress({ stage: 'save', message: 'Saving project...', progress: 90 });
      
      // Save to store
      useProjectStore.setState(project);
      
      // Save to IndexedDB
      await saveProjectToDB(project);
      
      onComplete();
      
    } catch (error) {
      console.error('Processing error:', error);
      setErrors([error instanceof Error ? error.message : 'Processing failed']);
      setCurrentStep('review');
    } finally {
      setIsProcessing(false);
    }
  };
  
  const renderStep = () => {
    switch (currentStep) {
      case 'upload':
        return (
          <div className="upload-step">
            <h2>Upload PCB Files</h2>
            <p>Upload a ZIP file or individual files:</p>
            <ul>
              <li>Gerber files (.gtl, .gbl, .gto, .gbo, etc.)</li>
              <li>Excellon drill files (.drl, .txt)</li>
              <li>Pick & Place files (.csv, .txt)</li>
              <li>BOM files (.csv, .xlsx)</li>
            </ul>
            
            <input
              type="file"
              multiple
              accept=".zip,.csv,.txt,.xlsx,.xls,.gbr,.gtl,.gbl,.gto,.gbo,.drl"
              onChange={(e) => handleFileUpload(e.target.files)}
              disabled={isProcessing}
            />
            
            {isProcessing && <p>Processing files...</p>}
            
            {errors.length > 0 && (
              <div className="errors">
                {errors.map((error, i) => (
                  <p key={i} className="error">{error}</p>
                ))}
              </div>
            )}
          </div>
        );
        
      case 'review':
        return (
          <div className="review-step">
            <h2>Review Detected Files</h2>
            <p>{files.length} files detected</p>
            
            <div className="files-table-container">
              <table>
                <thead>
                  <tr>
                    <th>Filename</th>
                    <th>Detected Type</th>
                    <th>Confidence</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((file) => (
                    <tr key={file.id}>
                      <td>{file.name}</td>
                      <td>{file.detectedRole}</td>
                      <td>{Math.round(file.confidence * 100)}%</td>
                      <td>
                        <button onClick={() => {/* TODO: Edit mapping */}}>
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="actions">
              <button onClick={() => setCurrentStep('upload')}>Back</button>
              <button 
                onClick={handleProcessFiles}
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing...' : 'Process Files'}
              </button>
            </div>
          </div>
        );
        
      case 'processing':
        return (
          <div className="processing-step">
            <h2>Processing Files...</h2>
            <div className="processing-progress-bar">
              <div 
                className="processing-progress-fill" 
                style={{ width: `${processingProgress.progress}%` }}
              />
            </div>
            <div className="processing-status">
              <p className="processing-stage">{processingProgress.message}</p>
              <p className="processing-percentage">{processingProgress.progress}%</p>
            </div>
            <p className="processing-hint">This may take a moment for large boards...</p>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="import-wizard">
      <div className="wizard-header">
        <h1>Import PCB Project</h1>
        <div className="step-indicator">
          <span className={currentStep === 'upload' ? 'active' : ''}>1. Upload</span>
          <span className={currentStep === 'review' ? 'active' : ''}>2. Review</span>
          <span className={currentStep === 'processing' ? 'active' : ''}>3. Process</span>
        </div>
      </div>
      
      <div className="wizard-content">
        {renderStep()}
      </div>
    </div>
  );
};
